import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  List,
  ListItem,
  ListItemText,
  Breadcrumbs,
  Link
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  TrendingUp as LevelIcon,
  Category as TypeIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Home as HomeIcon
} from '@mui/icons-material';
import { classInstanceAPI, memberProfileAPI } from '../../services/api';
import { ClassInstance, TierAccessLevel } from '../../types';
import { formatDate, formatTimeString } from '../../utils/dateUtils';
import { useRequiredMemberContext } from '../../hooks/useMemberContext';
import { Link as RouterLink } from 'react-router-dom';

const ClassBookingPage: React.FC = () => {
  const { instanceId } = useParams<{ instanceId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, memberProfile, isReady } = useRequiredMemberContext();

  const [classInstance, setClassInstance] = useState<ClassInstance | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [bookingLoading, setBookingLoading] = useState<boolean>(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false);
  const [bookingSuccess, setBookingSuccess] = useState<boolean>(false);
  const [cancellationSuccess, setCancellationSuccess] = useState<boolean>(false);
  const [tierAccessLevel, setTierAccessLevel] = useState<TierAccessLevel>(TierAccessLevel.FREE);
  const [cost, setCost] = useState<number | null>(null);
  const [existingBooking, setExistingBooking] = useState<any | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState<boolean>(false);
  const [cancelLoading, setCancelLoading] = useState<boolean>(false);
  const [cancellationPolicy, setCancellationPolicy] = useState<any | null>(null);

  // Get referrer for breadcrumbs
  const referrer = (location.state as any)?.from || 'calendar';

  useEffect(() => {
    if (instanceId && isReady && memberProfile?.id) {
      fetchClassInstance();
    }
  }, [instanceId, isReady, memberProfile?.id]);

  const fetchClassInstance = async () => {
    if (!instanceId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await classInstanceAPI.getInstance(instanceId);
      const instance = response.data;
      setClassInstance(instance);

      // Check if current member profile already has a booking for this instance
      // Backend filters bookings by X-Member-Profile-ID header, so we just check if any exist
      if (memberProfile?.id && instance.bookings && instance.bookings.length > 0) {
        const userBooking = instance.bookings.find(
          (b: any) => b.status !== 'CANCELLED'
        );
        setExistingBooking(userBooking || null);
      } else {
        setExistingBooking(null);
      }

      // Determine tier access level and cost
      if (memberProfile?.membershipTier) {
        const tierName = memberProfile.membershipTier.name;
        let accessLevel = TierAccessLevel.FREE; // Default to free

        if (instance.tierAccess && tierName) {
          const configuredAccess = instance.tierAccess[tierName];
          if (configuredAccess) {
            accessLevel = configuredAccess as TierAccessLevel;
          }
        } else if (instance.parentClass?.tierAccess && tierName) {
          const configuredAccess = instance.parentClass.tierAccess[tierName];
          if (configuredAccess) {
            accessLevel = configuredAccess as TierAccessLevel;
          }
        }

        setTierAccessLevel(accessLevel);

        // Set cost if paid access
        if (accessLevel === TierAccessLevel.PAID) {
          const instanceCost = instance.costOverride || instance.parentClass?.oneTimeCost || 0;
          setCost(Number(instanceCost));
        } else {
          setCost(null);
        }
      }
    } catch (error: any) {
      console.error('Error fetching class instance:', error);
      setError(error.response?.data?.message || 'Failed to load class details');
    } finally {
      setLoading(false);
    }
  };

  const handleBookClick = () => {
    setOpenConfirmDialog(true);
  };

  const handleConfirmBooking = async () => {
    if (!classInstance || !memberProfile) return;

    setBookingLoading(true);
    setError(null);

    try {
      await classInstanceAPI.bookInstance(classInstance.id, {
        memberProfileId: memberProfile.id,
        notes: ''
      });

      setBookingSuccess(true);
      setOpenConfirmDialog(false);

      // Close success message and redirect after 2 seconds
      setTimeout(() => {
        navigate('/dashboard', { state: { from: 'class-booking' } });
      }, 2000);
    } catch (error: any) {
      console.error('Error booking class:', error);
      setError(error.response?.data?.message || 'Failed to book class');
      setOpenConfirmDialog(false);
    } finally {
      setBookingLoading(false);
    }
  };

  const handleCancelDialog = () => {
    setOpenConfirmDialog(false);
  };

  const handleCancelBookingClick = async () => {
    if (!existingBooking) return;

    // Fetch cancellation policy first
    try {
      const policyResponse = await classInstanceAPI.getCancellationPolicyForInstanceBooking(
        instanceId!,
        existingBooking.id
      );
      setCancellationPolicy(policyResponse.data);
      setCancelDialogOpen(true);
    } catch (error: any) {
      console.error('Error fetching cancellation policy:', error);
      setError(error.response?.data?.message || 'Failed to load cancellation policy');
    }
  };

  const handleConfirmCancellation = async () => {
    if (!existingBooking || !instanceId) return;

    setCancelLoading(true);
    setError(null);

    try {
      const response = await classInstanceAPI.cancelOwnBooking(instanceId, existingBooking.id);
      
      // Show success message
      setCancellationSuccess(true);
      setCancelDialogOpen(false);
      setExistingBooking(null);

      // Show the response message which includes charge info
      if (response.data.message) {
        setError(null);
        // Redirect to dashboard after successful cancellation
        setTimeout(() => {
          navigate('/dashboard', { state: { from: 'class-booking' } });
        }, 3000);
      }
    } catch (error: any) {
      console.error('Error cancelling booking:', error);
      setError(error.response?.data?.message || 'Failed to cancel booking');
      setCancelDialogOpen(false);
    } finally {
      setCancelLoading(false);
    }
  };

  const handleCloseCancelDialog = () => {
    setCancelDialogOpen(false);
    setCancellationPolicy(null);
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error && !classInstance) {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 4 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button startIcon={<ArrowBackIcon />} onClick={handleBack}>
            Go Back
          </Button>
        </Box>
      </Container>
    );
  }

  if (!classInstance) {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 4 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Class not found
          </Alert>
          <Button startIcon={<ArrowBackIcon />} onClick={handleBack}>
            Go Back
          </Button>
        </Box>
      </Container>
    );
  }

  const isFullyBooked = (classInstance.enrolledCount || 0) >= classInstance.capacity;
  const canBook = !isFullyBooked && tierAccessLevel !== TierAccessLevel.NO_ACCESS && !existingBooking;
  const hasExistingBooking = !!existingBooking;

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, mb: 4 }}>
        {/* Success Message */}
        {bookingSuccess && (
          <Alert 
            severity="success" 
            icon={<CheckCircleIcon />}
            sx={{ mb: 3 }}
          >
            Successfully booked! Redirecting to dashboard...
          </Alert>
        )}

        {/* Cancellation Success Message */}
        {cancellationSuccess && (
          <Alert 
            severity="info" 
            icon={<CheckCircleIcon />}
            sx={{ mb: 3 }}
          >
            Booking cancelled successfully. Redirecting to dashboard...
          </Alert>
        )}

        {/* Existing Booking Alert */}
        {hasExistingBooking && !cancellationSuccess && (
          <Alert severity="info" sx={{ mb: 3 }}>
            You already have a booking for this class.
          </Alert>
        )}        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Main Content */}
        <Paper sx={{ p: 4 }}>
          {/* Header */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
              {classInstance.name}
            </Typography>
            {classInstance.description && (
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                {classInstance.description} aaaa
              </Typography>
            )}
          </Box>

          {/* Actions - Moved to top for mobile UX */}
          <Box sx={{ mb: 3, display: 'flex', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              disabled={bookingLoading || cancelLoading}
              sx={{ flex: { xs: '1 1 auto', sm: '0 0 auto' }, minWidth: { xs: 'auto', sm: 120 } }}
            >
              Back
            </Button>
            {hasExistingBooking ? (
              <Button
                variant="contained"
                color="error"
                size="large"
                onClick={handleCancelBookingClick}
                disabled={cancelLoading || cancellationSuccess}
                sx={{ flex: { xs: '1 1 auto', sm: '0 0 auto' }, minWidth: { xs: 'auto', sm: 150 } }}
              >
                {cancelLoading ? 'Cancelling...' : 'Cancel Booking'}
              </Button>
            ) : (
              <Button
                variant="contained"
                size="large"
                onClick={handleBookClick}
                disabled={!canBook || bookingLoading || bookingSuccess}
                sx={{ flex: { xs: '1 1 auto', sm: '0 0 auto' }, minWidth: { xs: 'auto', sm: 150 } }}
              >
                {isFullyBooked ? 'Class Full' : bookingLoading ? 'Booking...' : 'Book'}
              </Button>
            )}
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Class Details */}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <List disablePadding>
                {/* Date & Time - Combined */}
                <ListItem sx={{ px: 0, py: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 3 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flex: { xs: '1 1 auto', sm: '0 0 50%' } }}>
                      <CalendarIcon sx={{ mr: 2, color: 'primary.main', fontSize: 20 }} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>Date</Typography>
                        <Typography variant="body2" color="text.secondary">{formatDate(classInstance.sessionDate)}</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', flex: { xs: '1 1 auto', sm: '0 0 50%' } }}>
                      <ScheduleIcon sx={{ mr: 2, color: 'primary.main', fontSize: 20 }} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>Time</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatTimeString(classInstance.startTime)} - {formatTimeString(classInstance.endTime)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </ListItem>

                {/* Type & Level - Combined */}
                <ListItem sx={{ px: 0, py: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 3 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flex: { xs: '1 1 auto', sm: '0 0 50%' } }}>
                      <TypeIcon sx={{ mr: 2, color: 'primary.main', fontSize: 20 }} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>Type</Typography>
                        <Typography variant="body2" color="text.secondary">{classInstance.type || 'Not specified'}</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', flex: { xs: '1 1 auto', sm: '0 0 50%' } }}>
                      <LevelIcon sx={{ mr: 2, color: 'primary.main', fontSize: 20 }} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>Level</Typography>
                        <Typography variant="body2" color="text.secondary">{classInstance.level || 'Not specified'}</Typography>
                      </Box>
                    </Box>
                  </Box>
                </ListItem>

                {/* Venue */}
                <ListItem sx={{ px: 0, py: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <LocationIcon sx={{ mr: 2, color: 'primary.main', fontSize: 20 }} />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>Venue</Typography>
                      <Typography variant="body2" color="text.secondary">{classInstance.venue || 'Not specified'}</Typography>
                    </Box>
                  </Box>
                </ListItem>

                {/* Capacity */}
                <ListItem sx={{ px: 0, py: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <PersonIcon sx={{ mr: 2, color: 'primary.main', fontSize: 20 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>Capacity</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {classInstance.enrolledCount || 0} / {classInstance.capacity}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        label={isFullyBooked ? 'Full' : 'Available'}
                        color={isFullyBooked ? 'error' : 'success'}
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                </ListItem>
              </List>
            </Grid>
          </Grid>

          {/* Instructor */}
          {classInstance.instructor && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Instructor
              </Typography>
              <Typography variant="body1">
                {classInstance.instructor}
              </Typography>
            </Box>
          )}

          {/* Price Display */}
          {cost !== null && cost > 0 && (
            <Box sx={{ mt: 3, p: 2, bgcolor: 'info.lighter', borderRadius: 1 }}>
              <Typography variant="h6" color="primary" gutterBottom>
                Class Fee
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                ${typeof cost === 'number' ? cost.toFixed(2) : Number(cost).toFixed(2)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Payment will be processed after booking
              </Typography>
            </Box>
          )}

          {/* Access Level Warning */}
          {tierAccessLevel === TierAccessLevel.NO_ACCESS && (
            <Alert severity="warning" sx={{ mt: 3 }}>
              Your membership tier does not have access to this class. Please contact the front desk for more information.
            </Alert>
          )}

          {/* Booking Summary */}
          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Booking For
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
              {memberProfile?.firstName} {memberProfile?.lastName}
            </Typography>
            {memberProfile?.email && (
              <Typography variant="body2" color="text.secondary">
                {memberProfile.email}
              </Typography>
            )}
          </Box>
        </Paper>

        {/* Cancellation Confirmation Dialog */}
        <Dialog
          open={cancelDialogOpen}
          onClose={handleCloseCancelDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Cancel Booking
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" gutterBottom sx={{ mt: 2 }}>
              Are you sure you want to cancel your booking for this class?
            </Typography>
            
            {cancellationPolicy && (
              <Box sx={{ mt: 3 }}>
                {cancellationPolicy.isPastClass ? (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    This class has already occurred. You cannot cancel this booking.
                  </Alert>
                ) : (
                  <>
                    <Alert 
                      severity={cancellationPolicy.isWithinCancellationWindow ? 'info' : 'warning'} 
                      sx={{ mb: 2 }}
                    >
                      {cancellationPolicy.isWithinCancellationWindow ? (
                        <>
                          <strong>Good news!</strong> You are cancelling within the {cancellationPolicy.cancellationWindowHours}-hour cancellation window.
                          {cancellationPolicy.willBeCharged ? ' However, you will still be charged as payment has already been processed.' : ' No charges will be applied.'}
                        </>
                      ) : (
                        <>
                          <strong>Late cancellation:</strong> This class is less than {cancellationPolicy.cancellationWindowHours} hours away.
                        </>
                      )}
                    </Alert>

                    {cancellationPolicy.willBeCharged && (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        <strong>You will be charged ${cancellationPolicy.chargeAmount || '0.00'}</strong> because this cancellation is being made less than {cancellationPolicy.cancellationWindowHours} hours before the class.
                      </Alert>
                    )}

                    {!cancellationPolicy.willBeCharged && !cancellationPolicy.isWithinCancellationWindow && (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        In the future, please cancel at least {cancellationPolicy.cancellationWindowHours} hours before the class to give other members a fair chance at procuring a spot.
                      </Alert>
                    )}

                    <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="body2" gutterBottom>
                        <strong>Class:</strong> {classInstance.name}
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        <strong>Date:</strong> {formatDate(classInstance.sessionDate)}
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        <strong>Time:</strong> {formatTimeString(classInstance.startTime)} - {formatTimeString(classInstance.endTime)}
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        <strong>Hours until class:</strong> {cancellationPolicy.hoursUntilClass} hours
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseCancelDialog} disabled={cancelLoading}>
              Keep Booking
            </Button>
            <Button
              onClick={handleConfirmCancellation}
              variant="contained"
              color="error"
              disabled={cancelLoading || cancellationPolicy?.isPastClass}
              startIcon={cancelLoading ? <CircularProgress size={20} /> : null}
            >
              {cancelLoading ? 'Cancelling...' : 'Confirm Cancellation'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Booking Confirmation Dialog */}
        <Dialog
          open={openConfirmDialog}
          onClose={handleCancelDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Confirm Booking
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" gutterBottom>
              Are you sure you want to book this class?
            </Typography>
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" gutterBottom>
                <strong>Class:</strong> {classInstance.name}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Date:</strong> {formatDate(classInstance.sessionDate)}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Time:</strong> {formatTimeString(classInstance.startTime)} - {formatTimeString(classInstance.endTime)}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Member:</strong> {memberProfile?.firstName} {memberProfile?.lastName}
              </Typography>
              {cost !== null && cost > 0 && (
                <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold', color: 'primary.main' }}>
                  <strong>Amount:</strong> ${cost.toFixed(2)}
                </Typography>
              )}
            </Box>
            {cost !== null && cost > 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                A charge of ${cost.toFixed(2)} will be applied to your account.
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelDialog} disabled={bookingLoading}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmBooking}
              variant="contained"
              disabled={bookingLoading}
              startIcon={bookingLoading ? <CircularProgress size={20} /> : null}
            >
              {bookingLoading ? 'Booking...' : 'Confirm Booking'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default ClassBookingPage;
